import type { ZebraScannedCrate } from './useZebraScanner'
import type { CrateItem, OrderDetails } from '@/entities/order/model/order.types'
// NOTE: This is specific to Coles.
// It is not a generic crate handling utility.

export interface CrateType {
  type: string
  count: number
  icon: string
}

/**
 * Utilities for handling order crates
 */

// Define crate types (hsFields / non-Zebra list)
export const colesCrates = ['ambient', 'bulk ambient', 'bulk chilled', 'chilled', 'frozen', 'hot food']

/**
 * App-owned crate-type → icon map (Hello Screens source of truth).
 * Only the PM/QA-confirmed set is mapped; all other crate types render with no icon.
 * Payload/hsFields emojis are not used for crate-type rows.
 *
 * Confirmed mapping (crate-type names as used in Hello Screens / area-code mapping):
 * - Hot Food (09) → ♨️
 * - Chilled (21) → 🥶
 * - Bulk Ambient (02) → 📦
 * - Tobacco (41) / Liquor (42) — Restricted → 🆔
 * - Frozen (31) → 🧊
 */
export const colesCrateIconsMap = new Map<string, string>([
  ['hot food', '♨️'],
  ['chilled', '🥶'],
  ['bulk ambient', '📦'],
  ['tobacco', '🆔'],
  ['liquor', '🆔'],
  ['frozen', '🧊'],
])

/**
 * Gets the icon for a specific crate type from the app-owned map.
 */
export function getCrateIcon(crateType: string): string {
  return colesCrateIconsMap.get(crateType.toLowerCase()) || ''
}

/**
 * Filters the order.hsFields to only get crates that match the defined crate types
 * Handles both number and string values by parsing strings into numbers
 */
export function getCrates(hsFields?: Record<string, string | undefined>): Record<string, number> {
  if (!hsFields)
    return {}

  const filteredCrates: Record<string, number> = {}

  for (const crateType of colesCrates) {
    // Create a regex for exact, case-insensitive match
    const crateRegex = new RegExp(`^${crateType}$`, 'i')
    // Find the key in hsFields that matches the crate type
    const matchedKey = Object.keys(hsFields).find(key => crateRegex.test(key))
    if (matchedKey) {
      const value = hsFields[matchedKey]
      if (typeof value === 'string') {
        const parsedValue = Number.parseInt(value, 10)
        if (!Number.isNaN(parsedValue)) {
          filteredCrates[crateType] = parsedValue
        }
      }
    }
  }

  return filteredCrates
}

/**
 * Transforms the filtered crates object into an array of CrateType objects
 */
export function transformCratesToListItems(filteredCrates: Record<string, number>): CrateType[] {
  return Object.entries(filteredCrates).map(([type, count]) => ({
    type,
    count,
    icon: getCrateIcon(type),
  }))
}

/**
 * Get all crate types with their counts from order.hsFields
 * If a crate type isn't in hsFields, it will be included with count 0
 * Icons come from the app-owned crate-type map (payload emojis ignored).
 */
export function getAllCrateTypes(hsFields?: Record<string, string | undefined>): CrateType[] {
  const filteredCrates = getCrates(hsFields)

  return colesCrates.map(type => ({
    type,
    count: filteredCrates[type] || 0,
    icon: getCrateIcon(type),
  }))
}

/**
 * Calculate the total number of crates from a list of crate types
 */
export function calculateTotalCrates(crateTypes: CrateType[]): number {
  return crateTypes.reduce((sum, crate) => sum + crate.count, 0)
}

export function parseBarcodeToken(token: string): { orderId: string, areaCode: string, crateNumber: string } | null {
  const parts = token.split('*')
  if (parts.length !== 3) {
    return null
  }
  const [orderId, areaCode, crateNumber] = parts
  if (!orderId || !areaCode || !crateNumber) {
    return null
  }
  return { orderId, areaCode, crateNumber }
}

export function getExpectedCrateItemsFromArrayBarcodes(order?: OrderDetails): CrateItem[] {
  if (!order?.orderId) {
    return []
  }
  const mapping = order.areaCodeCrateTypeMapping ?? {}
  const barcodes = order.customEventMetaData?.array_barcodes?.split(',') ?? []

  if (barcodes.length === 0) {
    return []
  }
  return barcodes
    .map((token) => {
      const parsed = parseBarcodeToken(token)
      if (!parsed) {
        return null
      }
      if (parsed.orderId !== `A${order.orderId}`) {
        return null
      }
      const crateType = mapping[parsed.areaCode]
      if (!crateType) {
        return null
      }
      return {
        barcode: token,
        orderId: order.orderId,
        areaCode: parsed.areaCode,
        crateNumber: parsed.crateNumber,
        crateType,
      } as CrateItem
    })
    .filter((item): item is CrateItem => item !== null)
}

export function getAllCrateTypesFromExpectedCrateItems(
  expected: CrateItem[],
  areaCodeCrateTypeMapping?: Record<string, string | undefined>,
  _hsFields?: Record<string, string | undefined>,
): CrateType[] {
  const crateTypes = [...new Set(Object.values(areaCodeCrateTypeMapping ?? {}).filter((type): type is string => Boolean(type)))]
  const countsByType = new Map<string, number>()

  expected.forEach((item) => {
    const type = item.crateType.toLowerCase()
    countsByType.set(type, (countsByType.get(type) ?? 0) + 1)
  })

  return crateTypes.map(type => ({
    type,
    count: countsByType.get(type.toLowerCase()) ?? 0,
    icon: getCrateIcon(type),
  })).filter(crate => crate.count > 0)
}

/**
 * Expected crates for Zebra UI and client merge: prefers `array_barcodes` on the order,
 * then falls back to `order.crateScan.expectedBarcodes` so rows stay visible when metadata
 * is missing on a poll but scan state was reconciled from storage or a prior response.
 */
export function getExpectedCrateItemsForZebraOrder(order?: OrderDetails): CrateItem[] {
  const fromMeta = getExpectedCrateItemsFromArrayBarcodes(order)
  if (fromMeta.length > 0) {
    return fromMeta
  }
  if (!order?.orderId) {
    return []
  }
  const mapping = order.areaCodeCrateTypeMapping ?? {}
  const tokens = order.crateScan?.expectedBarcodes ?? []
  if (tokens.length === 0) {
    return []
  }
  return tokens
    .map(token => token.trim())
    .filter(Boolean)
    .map((token) => {
      const parsed = parseBarcodeToken(token)
      if (!parsed || parsed.orderId !== `A${order.orderId}`) {
        return null
      }
      const crateType = mapping[parsed.areaCode]
      if (!crateType) {
        return null
      }
      return {
        barcode: token,
        orderId: order.orderId,
        areaCode: parsed.areaCode,
        crateNumber: parsed.crateNumber,
        crateType,
      } as CrateItem
    })
    .filter((item): item is CrateItem => item !== null)
}

export function validateScannedCratesAgainstExpected(
  expected: CrateItem[],
  scanned: ZebraScannedCrate[],
): CrateItem[] {
  const expectedByBarcode = new Map(expected.map(item => [item.barcode, item]))
  const uniqueValid = new Map<string, CrateItem>()

  scanned.forEach((item) => {
    // Scanned orderId is the licence plate's A-prefixed segment; the crate keeps the actual order ID.
    const candidateBarcode = `${item.orderId}*${item.areaCode}*${item.crateNumber}`
    const expectedItem = expectedByBarcode.get(candidateBarcode)
    if (expectedItem) {
      uniqueValid.set(candidateBarcode, {
        barcode: candidateBarcode,
        orderId: expectedItem.orderId,
        areaCode: item.areaCode,
        crateNumber: item.crateNumber,
        crateType: expectedItem.crateType,
      })
    }
  })

  return [...uniqueValid.values()].filter(item => item.crateType)
}

export function getDynamicCrateTypesFromExpectedAndScanned(
  expected: CrateItem[],
  scanned: CrateItem[],
): CrateType[] {
  const expectedCounts = new Map<string, number>()
  const scannedCounts = new Map<string, number>()

  expected.forEach((item) => {
    expectedCounts.set(item.crateType, (expectedCounts.get(item.crateType) ?? 0) + 1)
  })
  scanned.forEach((item) => {
    scannedCounts.set(item.crateType, (scannedCounts.get(item.crateType) ?? 0) + 1)
  })

  return [...expectedCounts.entries()].map(([type, count]) => ({
    type,
    count,
    scannedCount: scannedCounts.get(type) ?? 0,
    icon: getCrateIcon(type),
  }))
}
